import type { ImageAIProvider, ProviderTier } from '../types/providers';
import type { OperationType } from '../types/operations';
import { HuggingFaceProvider } from './providers/HuggingFaceProvider';
import { ReplicateProvider } from './providers/ReplicateProvider';
import { FalProvider } from './providers/FalProvider';

export interface ProviderRegistryConfig {
  huggingfaceKey?: string;
  replicateKey?: string;
  falKey?: string;
  preferredTier: ProviderTier;
}

export class ProviderRegistry {
  private providers = new Map<string, ImageAIProvider>();
  private preferredTier: ProviderTier;

  constructor(config: ProviderRegistryConfig) {
    this.preferredTier = config.preferredTier;

    if (config.huggingfaceKey) {
      this.providers.set('huggingface', new HuggingFaceProvider(config.huggingfaceKey));
    }
    if (config.replicateKey) {
      this.providers.set('replicate', new ReplicateProvider(config.replicateKey));
    }
    if (config.falKey) {
      this.providers.set('fal', new FalProvider(config.falKey));
    }
  }

  getProvider(id: string): ImageAIProvider | undefined {
    return this.providers.get(id);
  }

  /** Find the best provider for an operation based on tier preference */
  getBestProvider(operation: OperationType): ImageAIProvider | null {
    // Collect providers that support this operation
    const candidates: ImageAIProvider[] = [];
    for (const provider of this.providers.values()) {
      if (provider.info.supportedOps.includes(operation)) {
        candidates.push(provider);
      }
    }

    if (candidates.length === 0) return null;

    // Prefer the tier the user selected
    const preferred = candidates.find(p => p.info.tier === this.preferredTier);
    if (preferred) return preferred;

    // Fall back to any available provider
    return candidates[0];
  }

  /** Get all providers that support a given operation */
  getProvidersForOp(operation: OperationType): ImageAIProvider[] {
    const result: ImageAIProvider[] = [];
    for (const provider of this.providers.values()) {
      if (provider.info.supportedOps.includes(operation)) {
        result.push(provider);
      }
    }
    return result;
  }

  /** Check if any provider is configured */
  hasAnyProvider(): boolean {
    return this.providers.size > 0;
  }

  /** List all configured providers */
  listProviders(): ImageAIProvider[] {
    return Array.from(this.providers.values());
  }

  setPreferredTier(tier: ProviderTier): void {
    this.preferredTier = tier;
  }
}
